import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DotsThreeVertical } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrors";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAGE_SIZE = 25;

export default function Cards() {
  const [items, setItems] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [merchantFilter, setMerchantFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      if (merchantFilter) params.merchant_id = merchantFilter;
      const r = await api.get("/admin/cards", { params });
      setItems(r.data.items);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, merchantFilter, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [statusFilter, merchantFilter, limit]);
  useEffect(() => { api.get("/admin/merchants", { params: { limit: 500 } }).then((r) => setMerchants(r.data.items)); }, []);

  const merchantsById = Object.fromEntries(merchants.map(m => [m.id, m.company_name]));

  const runAction = async (card, action) => {
    setOpenMenuId(null);
    if (action === "terminate" && !window.confirm("Terminate this card permanently?")) return;
    try {
      await api.post(`/admin/cards/${card.id}/${action}`);
      toast.success(action === "freeze" ? "Card frozen" : action === "unfreeze" ? "Card unfrozen" : "Card terminated");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed"));
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="cards-page">
        <PageHeader title="Cards" subtitle="All issued cards across the network." />

        <Surface className="p-3 mb-4 flex flex-wrap gap-2 items-center" testId="cards-filters">
          <Select label="Merchant" value={merchantFilter} onChange={setMerchantFilter} options={[{ v: "", l: "All merchants" }, ...merchants.map(m => ({ v: m.id, l: m.company_name }))]} testId="filter-merchant" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ v: "", l: "All" }, { v: "active", l: "Active" }, { v: "pending", l: "Pending" }, { v: "in_production", l: "In production" }, { v: "shipped", l: "Shipped" }, { v: "to_be_bound", l: "To be bound" }, { v: "pending_activation", l: "To be activated" }, { v: "frozen", l: "Frozen" }, { v: "locked", l: "Locked" }, { v: "closing", l: "Closing" }, { v: "expired", l: "Expired" }, { v: "terminated", l: "Closed" }]} testId="filter-status" />
          <div className="ml-auto text-xs text-zinc-500 tabular-nums">{total} cards</div>
        </Surface>

        <Surface testId="cards-table-card">
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Masked PAN</th>
                <th className="px-5 py-3">Merchant</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Expiry</th>
                <th className="px-5 py-3 text-right">Balance</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-xs text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((c) => (
                <tr key={c.id} data-testid={`card-row-${c.id}`} className="border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors duration-150">
                  <td className="px-5 py-3 font-mono text-xs">{c.masked_pan || "—"}</td>
                  <td className="px-5 py-3">{merchantsById[c.merchant_id] || c.merchant_id.slice(0, 8)}</td>
                  <td className="px-5 py-3 capitalize">{c.type}</td>
                  <td className="px-5 py-3"><StatusPill value={c.status} /></td>
                  <td className="px-5 py-3 font-mono text-xs">{c.expiry || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(c.balance)}</td>
                  <td className="px-5 py-3 text-right">
                    <CardActionsMenu
                      card={c}
                      open={openMenuId === c.id}
                      onOpenChange={(next) => setOpenMenuId(next ? c.id : null)}
                      onFreeze={() => runAction(c, "freeze")}
                      onUnfreeze={() => runAction(c, "unfreeze")}
                      onTerminate={() => runAction(c, "terminate")}
                    />
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-xs text-zinc-500">No cards yet. Cards are issued by merchants via the Proxy API.</td></tr>
              )}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </Surface>
      </div>
    </Layout>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs">
      <span className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-8 text-xs px-2 border border-zinc-200 rounded-sm bg-white focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function CardActionsMenu({ card, open, onOpenChange, onFreeze, onUnfreeze, onTerminate }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 160;
    const menuHeight = 132;
    const gap = 4;
    let top = rect.bottom + gap;
    let left = rect.right - menuWidth;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setMenuStyle({ position: "fixed", top, left, width: menuWidth, zIndex: 50 });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, placeMenu]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Card actions"
        aria-expanded={open}
        data-testid={`card-actions-${card.id}`}
        onClick={() => onOpenChange(!open)}
        className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors duration-150"
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && menuStyle && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={menuStyle}
          className="min-w-[10rem] rounded-sm border border-zinc-200 bg-white py-1 shadow-lg"
          data-testid={`card-actions-menu-${card.id}`}
        >
          <MenuItem onClick={onFreeze} testId={`freeze-${card.id}`}>Freeze</MenuItem>
          <MenuItem onClick={onUnfreeze} testId={`unfreeze-${card.id}`}>Unfreeze</MenuItem>
          <MenuItem onClick={onTerminate} testId={`terminate-${card.id}`} destructive>Terminate</MenuItem>
        </div>,
        document.body,
      )}
    </>
  );
}

function MenuItem({ children, onClick, testId, destructive }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      className={`block w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 transition-colors duration-150 ${
        destructive ? "text-red-700" : "text-zinc-900"
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
